import api from './api';
import login from './login';

const listeners = new Set();
function addEventListener(listener) {
    listeners.add(listener);
}

function removeEventListener(listener) {
    listeners.delete(listener);
}

function invokeListeners(key) {
    listeners.forEach(l => l({ key }));
}

const _cache = {};

// any time login status changes, clear the cache
login.addEventListener(clear);
function clear() {
    for (let key of Object.keys(_cache)) {
        delete _cache[key];
    }
    invokeListeners();
}

// sync the cache between windows
const channel = new BroadcastChannel('serverStorage.cache');
channel.onmessage = event => {
    const store = event?.data?.store;
    if (store?._userid) {
        // replace old promise with one to the updated cache
        _cache[store?._userid] = Promise.resolve(store);
        invokeListeners(event?.data?.key);
    }
};

async function load(userid, jwt) {
    // not logged in, for now throw error, we could also consider using local cache
    if (!jwt) throw new Error('Must be logged in to use this feature');

    // api call to download all key/values
    const response = await api.get('storage?jwt=' + encodeURIComponent(jwt));
    if (response.status !== 200) throw new Error('Unexpected error connecting to the server');

    const json = await response.json();

    // convert the array to a key/value map
    // also keep track of the userid of this cache
    const result = { '_userid': userid };
    for (let entry of json) {
        result[entry.key] = entry;
    }
    return result;
}

async function cache() {
    const jwt = login.jwt.get();
    const userid = login.sub();

    if (!_cache[userid]) {
        // setup a promise that will return key/value map for this userid
        // works even if the promise is reused multiple times
        _cache[userid] = load(userid, jwt);
    }

    // return the promise directly
    return _cache[userid];
}

// value will always be JSON
async function getItem(key) {
    const store = await cache();
    const value = store?.[key]?.value;
    if (value) return JSON.parse(JSON.stringify(value)); // clone
    return null;
}

// value must be JSON
async function setItem(key, value) {
    const store = await cache();

    const jwt = login.jwt.get();
    if (!jwt) throw new Error('Must be logged in to use this feature');

    const response = await api.post('storage?jwt=' + encodeURIComponent(jwt), { key, value, ifUnmodifiedSince: store[key]?.updated });
    if (response.status !== 200) throw new Error('Unexpected error connecting to the server');

    const entry = await response.json();
    if (entry.success) {
        // save succeeded, update cache and return true
        store[key] = { value, updated: entry.updated };
        channel.postMessage({ key, store }); // trigger cache update of other windows
        invokeListeners(key); // notify this window of changes
        return true;
    } else {
        // on failure, sync cache with server value, and return false
        store[key] = entry;
        return false;
    }
}

const storage = { getItem, setItem, addEventListener, removeEventListener };
export default storage;